import { Box, Grid, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import useAPI from "../../../../api/hooks/useAPI";
import LoadingIcon from "../../../../components/LoadingIcon";
import { ScreenshotAccordion } from "../../../../components/ScreenshotAccordion";
import { Toast } from "../../../../components/Toast";
import SecondaryButton from "../../../../components/buttons/SecondaryButton";
import useExpandedList from "../../../../components/hooks/ExpandedList";
import Guideline from "../components/Guideline";
import GuidelineStep from "../components/GuidelineStep";
import GuidelineStepByStep from "../components/GuidelineStepByStep";
import palo1 from "./images/palo1.png";
import palo2 from "./images/palo2.png";
import palo3 from "./images/palo3.png";
import palo4 from "./images/palo4.png";

import { useQueryClient } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import useIsIntegrated from "../../../../api/hooks/useIsIntegrated";
import { integrationKeys, onboardingKeys } from "../../../../api/queryKeys";
import { logos } from "../../environment/icons/iconExport";
import IntegrationGuideAlert from "../components/IntegrationGuideAlert";
import PaloAltoCortexXDRConnectForm from "./PaloAltoCortexXDRConnectForm";

const PaloAltoCortexXDRIntegration = () => {
  const [searchParams] = useSearchParams();
  const [guideMode, setGuideMode] = useState(false);

  useEffect(() => {
    if (searchParams.has("viewonly")) {
      setGuideMode(true);
    }
  }, [searchParams]);
  const { isIntegrated, isIntegratedLoading } = useIsIntegrated(
    "paloalto_cortex_xdr"
  );
  const [loading, setLoading] = useState(false);
  const queryClient = useQueryClient();
  const { addToExpandedList, handleExpandAll, handleCollapseAll } =
    useExpandedList();
  const { registerPaloAltoCortexXDRIntegration } = useAPI();

  const onSubmit = (values) => {
    setLoading(true);
    registerPaloAltoCortexXDRIntegration(
      values.api_key,
      values.api_url,
      values.key_id
    )
      .then((response) => {
        queryClient.invalidateQueries({
          queryKey: integrationKeys.all,
        });
        // only in EDR integrations, we need to invalidate the onboarding context
        queryClient.invalidateQueries({
          queryKey: onboardingKeys.all,
        });
        Toast.success("Palo Alto Cortex XDR integration successful");
      })
      .catch((error) => {
        if (error.response) {
          Toast.error(`Something went wrong: ${error.response.data.detail}`);
        } else {
          Toast.error("An error occurred while processing the request.");
        }
      })
      .finally(() => {
        setLoading(false);
      });
  };

  return (
    <Box>
      <Guideline
        title={
          !isIntegrated
            ? "Once integrated, the Breeze platform will automatically read the configurations from Palo Alto Cortex and prioritize the suggested remediations."
            : ""
        }
        image={logos.PaloAltoCortexLogo}
        name={"Palo Alto Cortex XDR"}
        content={
          loading || isIntegratedLoading ? (
            <LoadingIcon size={25} />
          ) : isIntegrated && !guideMode ? (
            <Box>
              <Typography variant={"h4"}>
                Palo Alto Cortex is integrated!
              </Typography>
            </Box>
          ) : (
            <div>
              {/* BUTTONS TO CONTROL EXPANSION */}
              <Box mb="20px">
                <Grid container spacing={2} direction={"row"}>
                  {guideMode && <IntegrationGuideAlert />}

                  <Grid item>
                    <SecondaryButton onClick={handleExpandAll}>
                      Expand All Screenshots
                    </SecondaryButton>
                  </Grid>
                  <Grid item>
                    <SecondaryButton onClick={handleCollapseAll}>
                      Collapse All Screenshots
                    </SecondaryButton>
                  </Grid>
                </Grid>
              </Box>
              {/* SETUP INSTRUCTIONS */}
              <GuidelineStepByStep>
                <GuidelineStep title="Create an API Key">
                  <Typography variant={"body2"}>
                    Sign in to Cortex XDR console as an administrator
                  </Typography>
                  <Typography variant={"body2"}>
                    Navigate to 
                    <strong>
                      {"Settings > Configurations > Integrations > API Keys"}
                    </strong>
                  </Typography>
                  <ScreenshotAccordion
                    images={[
                      {
                        file: palo1,
                        altext: "Palo Alto Configuration",
                      },
                    ]}
                    onExpanded={addToExpandedList}
                  />
                  <Typography variant={"body2"}>
                    Click <strong> New Key</strong> button.
                  </Typography>
                  <ScreenshotAccordion
                    images={[
                      {
                        file: palo2,
                        altext: "Palo Alto Cortex New Key",
                      },
                    ]}
                    onExpanded={addToExpandedList}
                  />
                </GuidelineStep>

                <GuidelineStep title="Configure the API Key">
                  <Typography variant={"body2"}>
                    Enter the following settings: <br />
                    Security Level: <strong>Advanced</strong> <br />
                    Role: <strong>Viewer</strong>
                  </Typography>
                  <ScreenshotAccordion
                    images={[
                      {
                        file: palo3,
                        altext: "Palo Alto Cortex Generate Key",
                      },
                    ]}
                    onExpanded={addToExpandedList}
                  />
                  <Typography variant={"body2"}>
                    Provide a comment for the new API token, for
                    example, ”Breeze Platform View only”.
                  </Typography>
                  <Typography variant={"body2"}>
                    Click <strong>Save</strong> and{" "}
                    <strong>copy the API key</strong>, then paste it into the
                    textbox below. Afterward, click <strong>Done</strong>.
                  </Typography>
                </GuidelineStep>

                <GuidelineStep title="Locate the API Key ID and API URL">
                  <Typography variant={"body2"}>
                    In the API Keys table, locate the ID field. <br />
                    Copy your corresponding <strong>ID </strong>number and paste
                    it into the textbox labeled "Key ID."
                  </Typography>
                  <Typography variant={"body2"}>
                    Click <strong>Copy API URL</strong> and{" "}
                    <strong>paste the API URL in the textbox</strong>.{" "}
                  </Typography>
                  <ScreenshotAccordion
                    images={[
                      {
                        file: palo4,
                        altext: "Palo Alto Cortex API Keys",
                      },
                    ]}
                    onExpanded={addToExpandedList}
                  />
                </GuidelineStep>

                <GuidelineStep title="Paste the API Key , API key ID and API URL">
                  <Box width={700}>
                    <PaloAltoCortexXDRConnectForm
                      onSubmit={onSubmit}
                      guideMode={guideMode}
                    />
                  </Box>
                </GuidelineStep>
              </GuidelineStepByStep>
            </div>
          )
        }
      />
    </Box>
  );
};

export default PaloAltoCortexXDRIntegration;
